import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const DISMISS_KEY = 'dd_assessment_promo_dismissed';

/**
 * AssessmentPromoBar — top-of-site slim bar promoting the quiz.
 *
 * Behaviour:
 * - Hidden on `/assessment` and `/ai-maturity-framework` (would be redundant)
 *  - Dismissable; state persists in sessionStorage so it doesn't nag
 *  - Slides in from the top after 800ms so it doesn't fight LCP
 */
export default function AssessmentPromoBar() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Skip if the user has already dismissed this session
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch {
      /* noop */
    }
    // Skip if already on the assessment route
    if (location.pathname.startsWith('/assessment') || location.pathname.startsWith('/ai-maturity-framework')) return;

    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const dismiss = () => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* noop */
    }
  };

  if (location.pathname.startsWith('/assessment') || location.pathname.startsWith('/ai-maturity-framework')) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="w-full bg-gradient-to-r from-[#1A1D21] via-[#1F2530] to-[#1A1D21] border-b border-[#4A90E2]/50 relative z-[60]"
          role="banner"
          data-testid="assessment-promo-bar"
        >
          <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
            <Link
              to="/assessment"
              className="flex items-center gap-3 text-sm md:text-base text-white/90 hover:text-white transition-colors flex-1 min-w-0"
              data-testid="assessment-promo-cta"
            >
              {/* Pulsing dot */}
              <span className="relative flex h-2.5 w-2.5 flex-shrink-0" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4A90E2] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#4A90E2]" />
              </span>
              <span className="truncate">
                <span className="hidden sm:inline text-white/70">Not sure where you stand? </span>
                <span className="font-semibold" style={{ fontFamily: 'Montserrat' }}>
                  Take our 5-min AI Maturity Quiz
                </span>
                <span className="text-[#4A90E2] font-bold ml-2" aria-hidden="true">→</span>
              </span>
            </Link>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss promo bar"
              data-testid="assessment-promo-dismiss"
              className="text-white/50 hover:text-white transition-colors flex-shrink-0 p-1"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
